import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "styled-components";
import {useOnClickOutside} from "../../hooks/useClickOutside";
import {Gap24, Input} from "../../components/ui/Content";
import {Body1, Subhead2} from "../../components/Font";
import { IOptionWithOwnInput, WindowSizePXEnum} from "../../interface/Basic/window";
import {InputIconContainer} from "../../components/ui/InputSearch";
import {DropDown, DropUp, GoBack, ModalCross} from "../../icon/Icon";
import {
    ActiveListItem,
    DropIconContainer,
    ListItem,
    ListItemContainer,
    MobileSelectListContainer,
    SelectDownList,
    SelectListContainer
} from "../../components/ui/Select";
import styled from "styled-components/macro";
import {InputSearch} from "../Input/InputSearch";
import {ModalActiveBtn} from "../../components/ui/Button";


interface SelectInputProps {
    items: IOptionWithOwnInput[]
    placeholder?: string
    onSelect: (value: IOptionWithOwnInput) => void
    onInput: (value: IOptionWithOwnInput) => void
    width?: string
    selectedItem: IOptionWithOwnInput | null
    inputValue: IOptionWithOwnInput | null
    onClean: () => void
    withOwnInput?: boolean
    isCleanable?: boolean
    className?: string
    mobileStyle?: string
    searchInfo?: {
        placeholder: string
        name: string
        listText: string
    }
}

export const SelectInput = ({
                                items,
                                placeholder,
                                onSelect,
                                onInput,
                                width = '100%',
                                selectedItem,
                                inputValue,
                                onClean,
                                withOwnInput,
                                isCleanable,
                                className,
                                mobileStyle,
                                searchInfo
                            }: SelectInputProps) => {
    const Theme = useTheme();
    const [selectOpened, setSelectOpened] = useState(false)
    const [mobileSelectOpened, setMobileSelectOpened] = useState(false)
    const [mobileInputOpened, setMobileInputOpened] = useState(false)
    const [filter, setFilter] = useState('')
    const ref = useRef<HTMLDivElement>(null);
    const toggle = () => setSelectOpened(prev => !prev)

    useOnClickOutside(ref, () => {
        if (selectedItem?.id === null && !withOwnInput) {
            onClean()
        }
        setSelectOpened(false)
    });

    const filteredData = items.filter(i => {
        return i.name.toLowerCase().split(' ').join('').includes(filter.toLowerCase().trim())
    })

    const [contentCoords, setContentCoords] = useState({
        top: 0,
        left: 0
    })
    const [contentWidth, setContentWidth] = useState(0)

    useEffect(() => {
        if (!selectOpened) {
            document.querySelector('.selectHeader')?.classList.remove('selectHeader')
            setFilter('')
        }
    }, [selectOpened])

    useEffect(() => {
        const resizer = () => {
            const header = document.querySelector('.selectHeader')
            if (header) {
                if (window.screen.availWidth <= parseInt(WindowSizePXEnum.xs) && className === 'mobile') {
                    setContentCoords({
                        top: header.getBoundingClientRect().bottom - 193,
                        left: header.getBoundingClientRect().left
                    })
                } else {
                    setContentCoords({
                        top: header.getBoundingClientRect().bottom + 3,
                        left: header.getBoundingClientRect().left
                    })
                }
            }
        }
        window.addEventListener("resize", resizer)
        window.addEventListener('scroll', resizer)
        return () => {
            window.removeEventListener('scroll', resizer)
            window.removeEventListener("resize", resizer);
        }
    }, []);

    return <SelectInputContainer
        className={className}
        style={{width: width}}>
        <div
            ref={ref}
            onClick={(e) => {
                if (!e.currentTarget.className.includes('selectHeader')) {
                    e.currentTarget.classList.add('selectHeader')
                }
                if (window.screen.availWidth <= parseInt(WindowSizePXEnum.xs) && className === 'mobile') {
                    setContentCoords({
                        top: e.currentTarget.getBoundingClientRect().bottom - 193,
                        left: e.currentTarget.getBoundingClientRect().left
                    })
                } else {
                    setContentCoords({
                        top: e.currentTarget.getBoundingClientRect().bottom + 3,
                        left: e.currentTarget.getBoundingClientRect().left
                    })
                }
                setContentWidth(e.currentTarget.offsetWidth)
                if (mobileStyle) {
                    setMobileSelectOpened(prev => !prev)
                }
                toggle()
            }}
        >
            <Input
                value={inputValue ? inputValue.name : ''}
                placeholder={placeholder}
                onInput={e => {
                    setFilter(e.currentTarget.value)
                    onInput({id: null, name: e.currentTarget.value})
                }}
            />

            <DropIconContainer>
                {selectOpened ? <DropUp/> : <DropDown/>}
            </DropIconContainer>

            {selectOpened && (filteredData.length !== 0 || !withOwnInput) &&
                <>
                    <SelectListContainer
                        display={mobileStyle ? 'none' : 'unset'}
                    >
                        <SelectDownList style={{
                            top: `${contentCoords.top.toString()}px`,
                            left: `${contentCoords.left.toString()}px`,
                            width: `${contentWidth.toString()}px`,
                        }}>
                            <ListItemContainer
                                style={{maxHeight: '200px'}}>
                                {filteredData.map((item, index) => {
                                    return <ListItem key={index}
                                                     as={item.id === selectedItem?.id ? ActiveListItem : ListItem}
                                                     onClick={() => {
                                                         onInput(item)
                                                         onSelect(item)
                                                     }}>
                                        <Body1>
                                            {item.name}
                                        </Body1>
                                    </ListItem>
                                })}
                            </ListItemContainer>
                            {filter !== '' && filteredData.length === 0 && !withOwnInput && <ListItem>
                                <Body1 color={Theme.color.secondary}>Ничего не найдено</Body1>
                            </ListItem>}
                        </SelectDownList>
                    </SelectListContainer>
                </>
            }
        </div>

        {mobileSelectOpened &&
            <MobileSelectListContainer display={mobileStyle ? 'unset' : 'none'}>
                <SelectDownList className={mobileStyle}>
                    <Gap24 style={{marginLeft: '20px', marginBottom: '30px'}}>
                        <GoBack onClick={() => setMobileSelectOpened(false)}/>
                        <Subhead2>{searchInfo?.name}</Subhead2>
                    </Gap24>
                    <InputSearch onChange={(value: string) => {
                        setFilter(value)
                    }}/>
                    <ListItemContainer
                        style={{maxHeight: '200px'}}>
                        {withOwnInput && <ListItem
                            onClick={() => {
                                setMobileSelectOpened(false)
                                setMobileInputOpened(true)
                            }}
                        >
                            <Body1>{searchInfo?.listText}</Body1>
                        </ListItem>}
                        {filteredData.map((item, index) => {
                            return <ListItem key={index}
                                             as={item.id === selectedItem?.id ? ActiveListItem : ListItem}
                                             onClick={() => {
                                                 onInput(item)
                                                 setMobileSelectOpened(false)
                                                 onSelect(item)
                                             }}>
                                <Body1>
                                    {item.name}
                                </Body1>
                            </ListItem>
                        })}
                    </ListItemContainer>
                    {filter !== '' && filteredData.length === 0 && <ListItem>
                        <Body1 color={Theme.color.secondary}>Ничего не найдено</Body1>
                    </ListItem>}
                </SelectDownList>
            </MobileSelectListContainer>
        }
        {
            mobileInputOpened &&
            <MobileSelectListContainer display={mobileStyle ? 'unset' : 'none'}>
                <SelectDownList>
                    <Gap24 style={{marginLeft: '20px', marginBottom: '30px'}}>
                        <GoBack onClick={() => {
                            onClean()
                            setMobileInputOpened(false)
                            setMobileSelectOpened(true)
                        }}/>
                        <Subhead2>Введите название</Subhead2>
                    </Gap24>
                    <InputSearch onChange={(value) => onInput({id: null, name: value})}
                                 placeholder={searchInfo?.placeholder}/>
                    <ModalActiveBtn
                        style={{width: '100%', height: '56px', justifyContent: 'center', marginTop: '36px'}}
                        onClick={() => setMobileInputOpened(false)}
                    >
                        Сохранить
                    </ModalActiveBtn>
                </SelectDownList>
            </MobileSelectListContainer>
        }
        {inputValue && inputValue.name !== '' && isCleanable &&
            <InputIconContainer
                style={{right: '50px'}}
                onClick={() => {
                    onInput({id: null, name: ''})
                    onSelect({id: null, name: ''})
                    onClean()
                }}>
                <ModalCross/>
            </InputIconContainer>}
    </SelectInputContainer>
}

interface SelectContainerProps {
    paddingRight?: string
}

const SelectInputContainer = styled.div<SelectContainerProps>`
  position: relative;
  user-select: none;

  input {
    padding-right: ${(style) => style.paddingRight && ''} !important;
    text-overflow: ellipsis;
  }
`

